// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useWebSocketContext, ConnectionStatus } from '../contexts/WebSocketContext';
import { useLobbyActions } from '../hooks/useLobbyActions';
import { useSettingsStore } from '../store/settingsStore';
import { Button } from './Button';
import { ChatPanel } from './chat';
import { AdvancedSettingsModal } from './lobby/AdvancedSettingsModal';
import { LeaveConfirmModal } from './lobby/LeaveConfirmModal';
import { LobbyToolbar } from './lobby/LobbyToolbar';
import { NpcManager } from './lobby/NpcManager';
import { PlayerListItem } from './lobby/PlayerListItem';
import { PresetSelector } from './lobby/PresetSelector';
import { SpectatorsSection } from './lobby/SpectatorsSection';
import { CharacterPicker } from './shared/CharacterPicker';
import type { PlayerInfo } from '../types/game';

// Note: onGameStart callback removed - navigation is now handled by WebSocketContext
export function Lobby() {
  const { sendMessage, status: wsStatus } = useWebSocketContext();
  const { configSchema, isAdvancedOpen, openAdvanced, closeAdvanced } = useSettingsStore();

  const {
    sessionId,
    playerId,
    lobbyState,
    spectators,
    allowSpectators,
    currentSettings,
    isStarting,
    isLeaving,
    advancedMode,
    copied,
    showLeaveConfirm,
    setShowLeaveConfirm,
    showNpcs,
    setShowNpcs,
    copiedClaimCode,
    selectingCharacter,
    characters,
    selectedCharacterId,
    allCharactersSelected,
    hasCharacterSelection,
    getDisplayRoomCode,
    getGameName,
    handleCopyLink,
    handleLeaveRoom,
    handleStartGame,
    handleTogglePrivacy,
    handleToggleSpectators,
    handleCopyClaimCode,
    handlePresetSelect,
    handleSaveSettings,
    handleSelectCharacter,
    currentPlayers,
    isHost,
  } = useLobbyActions();

  if (!lobbyState) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading lobby...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="lobby-container">
      {/* Magic Code Banner - Copyable (same as GameBoard) */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-1 text-center px-2">
        <button
          onClick={handleCopyLink}
          className="font-mono text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wider text-black hover:text-gray-800 transition-colors cursor-pointer truncate max-w-full"
          title="Click to copy join link"
          data-testid="lobby-room-code"
        >
          {getDisplayRoomCode()}
        </button>
        {copied && (
          <span className="ml-2 text-black text-sm" data-testid="lobby-copy-success">
            ✓ Copied!
          </span>
        )}
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="glass rounded-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-display text-3xl font-bold" data-testid="lobby-header">
              {getGameName()}
            </h1>
            <div className="flex items-center gap-2" data-testid="lobby-connection-status">
              <div
                className={`h-3 w-3 rounded-full ${
                  wsStatus === ConnectionStatus.CONNECTED
                    ? 'bg-green-500'
                    : wsStatus === ConnectionStatus.CONNECTING
                      ? 'bg-yellow-500 animate-pulse'
                      : 'bg-red-500'
                }`}
                data-testid="lobby-connection-indicator"
              />
              <span className="text-sm" data-testid="lobby-connection-text">
                {wsStatus === ConnectionStatus.CONNECTED
                  ? 'Connected'
                  : wsStatus === ConnectionStatus.CONNECTING
                    ? 'Connecting...'
                    : 'Disconnected'}
              </span>
            </div>
          </div>

          {/* Lobby Toolbar - Privacy, Spectators, NPCs, Settings */}
          <LobbyToolbar
            isPublic={lobbyState?.is_public ?? true}
            allowSpectators={allowSpectators}
            spectatorCount={spectators.length}
            showNpcs={showNpcs}
            isHost={isHost}
            onTogglePublic={handleTogglePrivacy}
            onToggleSpectators={handleToggleSpectators}
            onToggleNpcs={() => setShowNpcs(prev => !prev)}
            onOpenSettings={openAdvanced}
          />

          {/* Preset Selector - Host Only */}
          {isHost && currentSettings && (
            <PresetSelector
              gameId={lobbyState?.game_id || 'unknown'}
              currentSettings={currentSettings}
              onPresetSelect={handlePresetSelect}
              disabled={!isHost}
            />
          )}

          {/* Spectators Section */}
          {spectators.length > 0 && <SpectatorsSection spectators={spectators} />}

          {/* Character Selection - For games that require it (e.g., Terrible Psychiatrist) */}
          {hasCharacterSelection && (
            <div className="mb-6">
              <CharacterPicker
                characters={characters}
                selectedCharacterId={selectedCharacterId}
                playerId={playerId!}
                allPlayers={currentPlayers}
                onSelectCharacter={handleSelectCharacter}
                disabled={selectingCharacter}
              />
              {!allCharactersSelected && (
                <p className="text-center text-sm text-yellow-400 mt-3">
                  All players must select a character before starting
                </p>
              )}
            </div>
          )}

          {/* Advanced Section - Visible only when advanced mode is active (press ^ to toggle) */}
          {advancedMode && (
            <div className="mb-6 text-xs text-gray-400">
              <span className="font-medium">Session ID:</span>{' '}
              <code className="bg-black/20 px-1 rounded">{sessionId}</code>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4" data-testid="lobby-player-count">
              Players ({currentPlayers.length}/{lobbyState.max_players ?? '∞'})
            </h2>
            {lobbyState.min_players && currentPlayers.length < lobbyState.min_players && (
              <p className="text-sm text-yellow-400 mb-2" data-testid="lobby-min-players-warning">
                Need at least {lobbyState.min_players} players to start
              </p>
            )}
            <div className="space-y-2" data-testid="lobby-player-list">
              {currentPlayers.map((player: PlayerInfo) => (
                <PlayerListItem
                  key={player.player_id}
                  player={player}
                  isCurrentUser={player.player_id === playerId}
                  isFirstPlayer={player.player_id === currentPlayers[0]?.player_id}
                  isHost={isHost}
                  copiedClaimCode={copiedClaimCode}
                  onCopyClaimCode={handleCopyClaimCode}
                />
              ))}
            </div>
          </div>

          {/* NPC Manager - Host Only, visible when showNpcs is true */}
          {isHost && showNpcs && <NpcManager />}

          <div className="flex gap-4 mt-8">
            {isHost ? (
              <Button
                variant="success"
                onClick={handleStartGame}
                disabled={
                  isStarting ||
                  currentPlayers.length < (lobbyState.min_players ?? 1) ||
                  (hasCharacterSelection && !allCharactersSelected)
                }
                className="flex-1"
                data-testid="lobby-start-game-button"
              >
                {isStarting ? 'Starting...' : 'Start Game'}
              </Button>
            ) : (
              <div
                className="flex-1 text-center py-2 bg-white/5 rounded-lg"
                data-testid="lobby-waiting-message"
              >
                <p className="text-sm text-gray-300">Waiting for host to start the game...</p>
              </div>
            )}

            <Button
              variant="secondary"
              onClick={() => setShowLeaveConfirm(true)}
              disabled={isLeaving}
              data-testid="lobby-leave-button"
            >
              {isLeaving ? 'Leaving...' : 'Leave'}
            </Button>
          </div>
        </div>
      </div>

      {/* Leave Confirmation Modal */}
      <LeaveConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleLeaveRoom}
      />

      {/* Advanced Settings Modal */}
      {currentSettings && configSchema && (
        <AdvancedSettingsModal
          isOpen={isAdvancedOpen}
          onClose={closeAdvanced}
          gameId={lobbyState?.game_id || 'unknown'}
          currentSettings={currentSettings}
          configSchema={configSchema}
          currentPlayerCount={currentPlayers.length}
          onSave={handleSaveSettings}
        />
      )}

      {/* Chat Panel */}
      <ChatPanel sendMessage={sendMessage} />
    </div>
  );
}
